"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.patientPageSchema = exports.patientPageRequestSchema = exports.PATIENT_SORT_COLUMNS = exports.PATIENT_PAGE_SIZE_MAX = exports.PATIENT_PAGE_SIZE = exports.patientRowSchema = exports.patientListQuerySchema = exports.patientListParamsSchema = exports.PATIENT_STATUSES = void 0;
// Wire shapes of Screen 4's routes (SPEC §8 Screen 4, §9 patient endpoints; J-037), served at
// `/api/v1/performance-groups/:groupId/measures/:measureKey/patients` (with `format=csv` for the
// export) (D-973); the instance expansion left with the F1 files (J-184). THESE CARRY PHI: a patient's name and
// date of birth. They are served only to a caller with `acoPatients` and `phi_access` (SPEC §15.3,
// CR-001 CRQ-024), every call is audited (D-978), and none of these values may be logged, put in a
// URL or written to `audit_events.details` (`.claude/rules/phi.md`). The only patient identifier in a
// URL is `patientId`, the opaque `core.patient.patient_id` uuid (D-975). Numbers are raw (SPEC §9).
const zod_1 = require("zod");
const inactive_js_1 = require("../quality-report/inactive.js");
const groups_js_1 = require("./groups.js");
/** The value list of `core.group_patient_measure`'s and `core.measure_instance`'s status CHECKs. */
exports.PATIENT_STATUSES = ["MET", "NOT_MET", "EXCEPTION", "EXCLUDED", "INCOMPLETE"];
const patientStatusSchema = zod_1.z.enum(exports.PATIENT_STATUSES);
const measureKeySchema = zod_1.z.coerce.number().int().positive().max(2_147_483_647);
// No `.meta({ id })` on a querystring or params schema: @fastify/swagger expands parameters inline.
exports.patientListParamsSchema = groups_js_1.performanceGroupParamsSchema.extend({
    measureKey: measureKeySchema,
});
exports.patientListQuerySchema = zod_1.z.object({
    /**
     * J-268: one ACO's code, for a group whose practices span ACOs: only that ACO's patients. Omitted
     * means every ACO of the group in the caller's scope ("Both").
     */
    aco: zod_1.z.string().min(1).max(64).optional(),
    /** One final status; omitted means every status. */
    status: patientStatusSchema.optional(),
    /** One `ref.measure_stratum.stratum_code` of the measure (Screen 2's `stratumCode`, e.g. `1a`). */
    stratum: zod_1.z
        .string()
        .regex(/^[0-9A-Za-z]{1,16}$/)
        .optional(),
    /**
     * `csv` returns the same rows as a download, audited as an `export` (SPEC §8 Screen 4); J-248: `xlsx`
     * the same rows as a workbook, the performance group page's download.
     */
    format: zod_1.z.enum(["json", "csv", "xlsx"]).default("json"),
});
/** One Screen 4 row: one patient's final status for the group and measure (SPEC §4.4, §8). */
exports.patientRowSchema = zod_1.z
    .object({
    patientId: zod_1.z.uuid(),
    firstName: zod_1.z.string(),
    lastName: zod_1.z.string(),
    /** ISO `YYYY-MM-DD`. */
    dob: zod_1.z.iso.date(),
    finalStatus: patientStatusSchema,
    /** The patient's stratum codes (`core.group_patient_stratum`), in the strata's `display_order`. */
    strata: zod_1.z.array(zod_1.z.string()),
    /** The deciding A row's owner TIN (J-184); empty when no A row decided the row. */
    billingTins: zod_1.z.array(zod_1.z.string()),
    /** The deciding A row's owner NPI, when it names one. */
    renderingNpis: zod_1.z.array(zod_1.z.string()),
    /** The deciding A row's last qualifying visit. */
    latestEncounterDate: zod_1.z.iso.date().nullable(),
    /** An A row carries no result value (null); its deciding date is the result date. */
    resultValue: zod_1.z.number().nullable(),
    resultDate: zod_1.z.iso.date().nullable(),
    /** `core.group_patient_measure.instance_count`: the A row's qualifying visits. */
    instanceCount: zod_1.z.number().int(),
    /** Seen under more than one TIN: `distinct_tin_count` > 1 (the Multi-TIN badge). */
    multiTin: zod_1.z.boolean(),
    /**
     * J-268: the ACO the row counts in, by code: a group whose practices span ACOs lists patients of
     * each; null for a row calculated before the ACO was recorded (its group's own ACO).
     */
    acoCode: zod_1.z.string().nullable(),
    /** J-274 (NR-05): a date of death or discharge is on the deciding A row's person line. */
    inactive: zod_1.z.boolean(),
    /** J-274: Deceased (winning when both are on file) or Discharged; null when active. */
    inactiveReason: zod_1.z.enum(inactive_js_1.INACTIVE_REASONS).nullable(),
    /** J-274: the date of death, else of discharge, ISO; null when active. */
    inactiveDate: zod_1.z.iso.date().nullable(),
})
    .meta({ id: "AcoPatientRow" });
// ---- the paged group patient list (J-202) ----
/** Rows per page of the group patient list unless the request asks for another size (J-202). */
exports.PATIENT_PAGE_SIZE = 200;
/** The largest page a request may ask for. */
exports.PATIENT_PAGE_SIZE_MAX = 500;
/** Columns the paged list sorts by; `default` is the list's own order (NOT_MET and INCOMPLETE first). */
exports.PATIENT_SORT_COLUMNS = [
    "default",
    "name",
    "dob",
    "status",
    "visit",
    // J-274: the Inactive columns sort like the others.
    "inactive",
    "inactiveReason",
    "inactiveDate",
];
/**
 * `POST …/measures/:measureKey/patient-pages`: one page of the list. A POST because the search text can be
 * a patient's name, which never goes into a URL (`.claude/rules/phi.md` rule 2).
 */
exports.patientPageRequestSchema = zod_1.z.object({
    /**
     * J-268: one ACO's code, for a group whose practices span ACOs: only that ACO's patients. Omitted
     * means every ACO of the group in the caller's scope ("Both").
     */
    aco: zod_1.z.string().min(1).max(64).optional(),
    /** One final status; omitted means every status. */
    status: patientStatusSchema.optional(),
    /** One stratum code of the measure, as on the GET list. */
    stratum: zod_1.z
        .string()
        .regex(/^[0-9A-Za-z]{1,16}$/)
        .optional(),
    /** Matched, ignoring case, against the last name, first name and "last, first". */
    search: zod_1.z.string().trim().min(1).max(100).optional(),
    /** J-274: only inactive (`Y`) or only active (`N`) patients. */
    inactive: zod_1.z.enum(["Y", "N"]).optional(),
    /** J-274: only the patients inactive for this reason. */
    inactiveReason: zod_1.z.enum(inactive_js_1.INACTIVE_REASONS).optional(),
    sort: zod_1.z
        .object({ column: zod_1.z.enum(exports.PATIENT_SORT_COLUMNS), direction: zod_1.z.enum(["asc", "desc"]) })
        .optional(),
    page: zod_1.z.number().int().min(1).max(1_000_000).default(1),
    pageSize: zod_1.z.number().int().min(1).max(exports.PATIENT_PAGE_SIZE_MAX).default(exports.PATIENT_PAGE_SIZE),
});
exports.patientPageSchema = zod_1.z
    .object({
    /** Rows that match the status, stratum and search, over every page. */
    total: zod_1.z.number().int(),
    page: zod_1.z.number().int(),
    pageSize: zod_1.z.number().int(),
    rows: zod_1.z.array(exports.patientRowSchema),
})
    .meta({ id: "AcoPatientPage" });
//# sourceMappingURL=patients.js.map